import { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui'
import { getBlogPost } from '@/lib/blog'
import { trackBlogView } from '@/lib/blog-analytics'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { CoverImage } from '@/components/CoverImage'
import { Calendar, ArrowLeft } from 'lucide-react'
import type { Components } from 'react-markdown'

const CALENDLY_URL = '/book'

const mdComponents: Components = {
  h1: ({ children }) => <h1 className="mt-10 mb-6 text-3xl font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-10 mb-3 text-xl font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-6 mb-2 text-lg font-medium">{children}</h3>,
  p: ({ children }) => <p className="text-muted-foreground mb-4 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="text-muted-foreground mb-4 ml-6 list-outside list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="text-muted-foreground mb-4 ml-6 list-outside list-decimal space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="text-foreground font-semibold">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-primary/30 text-muted-foreground my-4 border-l-4 pl-4 italic">{children}</blockquote>
  ),
  hr: () => <hr className="border-border my-8" />,
  table: ({ children }) => (
    <div className="mb-4 overflow-x-auto">
      <table className="text-muted-foreground w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-border text-foreground border px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border-border border px-3 py-2">{children}</td>,
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getBlogPost(slug) : undefined
  const breadcrumbs = generateBreadcrumbs(`/blog/${slug}`, breadcrumbLabels)

  useSEO(
    post
      ? {
          title: post.title,
          description: post.description,
          url: `/blog/${post.slug}`,
          canonical: `/blog/${post.slug}`,
          type: 'article',
          breadcrumbs,
          article: {
            publishedTime: post.date,
            author: post.author,
            section: post.category,
            tags: post.tags,
          },
          schema: {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            author: {
              '@type': 'Person',
              name: post.author,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Fugue Lab',
              url: 'https://fuguelab.com',
            },
          },
        }
      : { title: 'Post Not Found' }
  )

  useEffect(() => {
    if (post) {
      trackBlogView(post.slug, post.category)
    }
  }, [post])

  if (!post) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-20 text-center">
        <span className="text-primary/20 block text-6xl select-none" aria-hidden>
          &#9833;
        </span>
        <h1 className="text-3xl font-bold">Post Not Found</h1>
        <p className="text-muted-foreground">This blog post doesn't exist or may have been moved.</p>
        <Link to="/blog" className="text-primary inline-flex items-center gap-2 hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to all posts
        </Link>
      </div>
    )
  }

  return (
    <article className="mx-auto max-w-3xl px-4 py-12">
      {/* Back link */}
      <Link
        to="/blog"
        className="text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1 text-sm transition"
      >
        <ArrowLeft className="h-4 w-4" />
        All posts
      </Link>

      {/* Header */}
      <header className="mb-10 space-y-4">
        <div className="text-muted-foreground flex items-center gap-3 text-sm">
          <span className="bg-primary/10 text-primary rounded px-2 py-1 text-xs font-medium">{post.category}</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            {new Date(post.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </div>
        <h1 className="text-4xl leading-tight font-bold">{post.title}</h1>
        <p className="text-muted-foreground text-lg leading-relaxed">{post.description}</p>
        <p className="text-muted-foreground text-sm">By {post.author}</p>
      </header>

      {/* Cover */}
      <div className="mb-10">
        <CoverImage slug={post.slug} alt={post.title} />
      </div>

      {/* Content - strip leading H1 since the header already shows the title */}
      <div>
        <Markdown remarkPlugins={[remarkGfm]} components={mdComponents}>
          {post.content.replace(/^#\s+.+\n+/, '')}
        </Markdown>
      </div>

      {/* Calendly CTA */}
      <section className="border-border mt-16 space-y-4 border-t pt-10 text-center">
        <h2 className="text-2xl font-semibold">Ready to modernize your website?</h2>
        <p className="text-muted-foreground mx-auto max-w-lg">
          Book a free 15-minute call. We'll review your current site and identify the biggest opportunities for your
          organization.
        </p>
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
          <Button size="lg" className="mt-2 cursor-pointer">
            Book a Free Call
          </Button>
        </a>
      </section>

      {/* Tags */}
      {post.tags.length > 0 && (
        <div className="border-border mt-10 flex flex-wrap gap-2 border-t pt-6">
          {post.tags.map((tag) => (
            <span key={tag} className="bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs">
              {tag}
            </span>
          ))}
        </div>
      )}
    </article>
  )
}
